import React, { useEffect, useState } from 'react';
import Select, { MultiValue, SingleValue } from 'react-select';
import DatePicker from 'react-datepicker';

import './reportFilter.scss';

import { Tag_Delete, refresh } from "@/images";
import LanguageProvider from '@/providers/languageProvider';
import ReservationInsightsFilter from '../../interfaces/reservationInsightsFilter';
import IReactSelectValue from '@/interfaces/IReactSelectValue';
import MultiSelectCheckBoxes from '../multiSelectCheckboxes/multiSelectCheckboxes';
import FilterUtils from '@/pages/realestateutilization/tabs/reportfilter/filter.utils';
import { Floor, Venue, Workspace, Room } from '../../interfaces/filterOptions';
import SelectBoxUtils from '@/utils/selectBoxUtils';
import ChartUtils from '../charts/chartUtils';
import CenteredPageLoader from '@/components/loaders/centeredPageLoader';
import SliderComponent from '@/components/slider/sliderComponent';

interface ReportFilterProps {
    id?: string;
    loading: boolean;
    onUpdate: (filter: ReservationInsightsFilter) => void;
    venues: Venue[];
    floors: Floor[];
    workspaces: Workspace[];
    rooms: Room[];
    filterOptionsChanged: number;
    triggerUpdate: number;
}

const ReportFilter: React.FC<ReportFilterProps> = (props) => {

    const workspaceOrRoomFilterOptions: IReactSelectValue[] = [
        { value: 'both', label: `${LanguageProvider.getTranslation('mapdetails.workspaces')} & ${LanguageProvider.getTranslation('mapdetails.rooms')} ` },
        { value: 'rooms', label: LanguageProvider.getTranslation('mapdetails.rooms') },
        { value: 'workplaces', label: LanguageProvider.getTranslation('mapdetails.workspaces') }
    ]
    const [filter, setFilter] = useState<ReservationInsightsFilter>(new ReservationInsightsFilter());
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [minEndDate, setMinEndDate] = useState<Date | undefined>();

    const [weekOptions, setWeekOptions] = useState<IReactSelectValue[]>([]);
    const [dayOfWeekOptions] = useState<IReactSelectValue[]>(FilterUtils.getDayOfWeekOptions());
    const [holidayOptions] = useState<IReactSelectValue[]>(FilterUtils.getHolidayOptions());
    const [venueOptions, setVenueOptions] = useState<IReactSelectValue[]>([]);
    const [floorOptions, setFloorOptions] = useState<IReactSelectValue[]>([]);
    const [departmentOptions, setDepartmentOptions] = useState<IReactSelectValue[]>([]);
    const [placeOptions, setPlaceOptions] = useState<IReactSelectValue[]>([]);
    const [placeTypeOptions, setPlaceTypeOptions] = useState<IReactSelectValue[]>([]);

    useEffect(() => {
        const savedFilter = localStorage.getItem('reservationFilter');
        const allVenues = props.venues.map(venue => ({ value: venue.id, label: venue.displayName }));
        let newFilter = new ReservationInsightsFilter();

        if (savedFilter) {
            const parsedFilter = JSON.parse(savedFilter);
            const filterWithDates = ChartUtils.convertDates(parsedFilter) as ReservationInsightsFilter;

            newFilter = filterWithDates;
        } else {
            newFilter = getDefaultFilter();
        }

        setVenueOptions(allVenues);
        setFilter(newFilter);
        getSortedAndFilteredOptions(newFilter);
        setStartEndDates(newFilter.startDate!, newFilter.endDate!);

        localStorage.setItem('reservationFilter', JSON.stringify(newFilter));

        props.onUpdate(newFilter);

        // eslint-disable-next-line
    }, [props.filterOptionsChanged]);

    useEffect(() => {
        const savedFilter = localStorage.getItem('reservationFilter');

        if (!savedFilter) {
            setIsDirty(false);
            saveFilterToLocalStorage()
        } else {
            const isDirty = JSON.stringify(filter) !== savedFilter;

            setIsDirty(isDirty);
        }

    }, [filter]);

    function getSortedAndFilteredOptions(filter: ReservationInsightsFilter): ReservationInsightsFilter {

        const selectedVenueIds = filter.venues.length ? filter.venues.map(venue => venue.value) : props.venues.map(venue => venue.id);
        const selectedFloorIds = filter.floors ? filter.floors.map(floor => floor.value) : [];
        const selectedDepartmentIds = filter.departments ? filter.departments.map(department => department.value) : [];
        const selectedWorkplaceIds = filter.workPlaces ? filter.workPlaces.map(place => place.value) : [];
        const selectedRoomIds = filter.rooms ? filter.rooms.map(room => room.value) : [];

        // Gather valid floors
        const validFloors = props.floors.filter(floor => selectedVenueIds.includes(floor.venueId));
        const validFloorOptions = validFloors.map(floor => ({ value: floor.id, label: floor.displayName }))
        const selectedFloorOptions = validFloorOptions.filter(floor => selectedFloorIds.includes(floor.value));
        const selectedValidFloorOptions = selectedFloorOptions.length ? selectedFloorOptions : validFloorOptions; // No floors selected, so all floors are valid
        const selectedValidFloorOptionIds = selectedValidFloorOptions.map(floor => floor.value);

        filter.floors = selectedFloorOptions;
        setFloorOptions(validFloorOptions);

        // Gather valid departments
        const validDepartments = [...new Set(validFloors.filter(floor => selectedValidFloorOptionIds.includes(floor.id)).flatMap(floor => floor.departments))];
        const validDepartmentOptions = validDepartments.map(department => ({ value: department, label: department }));
        const selectedDepartmentOptions = validDepartmentOptions.filter(department => selectedDepartmentIds.includes(department.value));
        const selectedValidDepartmentOptionIds = selectedDepartmentOptions.length ? selectedDepartmentOptions.map(department => department.value) : validDepartmentOptions.map(department => department.value);

        filter.departments = selectedDepartmentOptions;
        setDepartmentOptions(validDepartmentOptions);

        // Gather valid places
        const validWorkSpaces = !FilterUtils.IncludeWorkplaces(filter) ? []: props.workspaces
            .filter(workspace => selectedVenueIds.includes(workspace.venueId)
                && selectedValidFloorOptionIds.includes(workspace.floorId)
                && workspace.departments.some(department => selectedValidDepartmentOptionIds.includes(department)));
        const validWorkSpaceOptions = validWorkSpaces.map(workspace => ({ value: workspace.id, label: workspace.displayName }));
        const newSelectedWorkSpaceOptions = validWorkSpaceOptions.filter(workspace => selectedWorkplaceIds.includes(workspace.value));

        const validRooms = !FilterUtils.IncludeRooms(filter) ? [] : props.rooms
            .filter(room => selectedVenueIds.includes(room.venueId)
                && selectedValidFloorOptionIds.includes(room.floorId)
                && room.departments.some(department => selectedValidDepartmentOptionIds.includes(department)));
        const validRoomOptions = validRooms.map(room => ({ value: room.id, label: room.displayName }));
        const newSelectedRoomOptions = validRoomOptions.filter(room => selectedRoomIds.includes(room.value));

        filter.workPlaces = newSelectedWorkSpaceOptions;
        filter.rooms = newSelectedRoomOptions;
        setPlaceOptions(validWorkSpaceOptions.concat(validRoomOptions));

        // Gather valid placetypes
        const uniqueWorkspaceTypes = new Set<string>();
        const uniqueRoomTypes = new Set<string>();

        validRooms.forEach(room => { room.types.forEach(type => uniqueRoomTypes.add(type)); });
        validWorkSpaces.forEach(workspace => { workspace.types.forEach(type => uniqueWorkspaceTypes.add(type)); });

        const validWorkspaceTypeOptions = Array.from(uniqueWorkspaceTypes).map(type => ({ value: "workplace_" + type, label: type }));
        const validRoomTypeOptions = Array.from(uniqueRoomTypes).map(type => ({ value: "room_" + type, label: type }));

        setPlaceTypeOptions(validWorkspaceTypeOptions.concat(validRoomTypeOptions)); 

        return filter;
    }

    function handleStartDateChange(newStartDate: Date | null): void {

        setStartEndDates(newStartDate!, filter.endDate!);

        setFilter({ ...filter, startDate: newStartDate || undefined });
    }

    function handleEndDateChange(newEndDate: Date | null): void {

        setStartEndDates(filter.startDate!, newEndDate!);

        setFilter({ ...filter, endDate: newEndDate || undefined });
    }

    function setStartEndDates(startDate: Date, endDate: Date): void {
        const minEndDate = Math.min(startDate.getTime(), new Date().getTime());

        const validWeekOptions = FilterUtils.getWeekOptions(startDate, endDate);
        const selectedWeekOptions = (filter.weeks ?? []).filter(week => validWeekOptions.some(validWeek => validWeek.value === week.value));

        filter.weeks = selectedWeekOptions;
        setWeekOptions(validWeekOptions);
        setMinEndDate(new Date(minEndDate));
    }

    function handleWeeksChanged(weeks: MultiValue<IReactSelectValue>): void {
        setFilter({ ...filter, weeks: weeks.map(item => item) });
    }

    function handleDayOfWeekChanged(days: MultiValue<IReactSelectValue>): void {
        setFilter({ ...filter, weekdays: days.map(item => item) });
    }

    function handleHolidayChanged(holidays: MultiValue<IReactSelectValue>): void {
        setFilter({ ...filter, holidaysAsList: holidays.map(item => item) });
    }

    function handleHourChanged(values: readonly number[]): void {
        setFilter({ ...filter, startHourOfTheDay: values[0], endHourOfTheDay: values[1] });
    }

    function handleVenueChanged(venues: MultiValue<IReactSelectValue>): void {

        const newFilter = { ...filter, venues: venues.map(item => item) };
        getSortedAndFilteredOptions(newFilter);

        setFilter(newFilter);
    }

    function handleFloorChanged(floors: MultiValue<IReactSelectValue>): void {

        const newFilter: ReservationInsightsFilter = {...filter, floors: floors ? floors.map(item => item) : []};
        getSortedAndFilteredOptions(newFilter);

        setFilter({ ...newFilter, floors: newFilter.floors });
    }

    function handleDepartmentChanged(departments: MultiValue<IReactSelectValue>): void {

        const newFilter: ReservationInsightsFilter = {...filter,departments: departments ? departments.map(item => item) : []}
        getSortedAndFilteredOptions(newFilter);

        setFilter({ ...filter, departments: newFilter.departments });
    }

    function handlePlaceChanged(places: MultiValue<IReactSelectValue>): void {

        const selectedWorkplaces = places.filter(place => props.workspaces.some(workspace => workspace.id === place.value));
        const selectedRooms = places.filter(place => props.rooms.some(room => room.id === place.value));

        setFilter({ ...filter, workPlaces: selectedWorkplaces, rooms: selectedRooms });
    }

    function handlePlaceTypesChanged(placeTypes: MultiValue<IReactSelectValue>): void {
        setFilter({ ...filter, allPlaceTypes: placeTypes.map(item => item) });
    }

    function handleWorkspaceOrRoomFilterOptionChanged(selected: SingleValue<IReactSelectValue>): void {

        if (selected) {
            const newFilter: ReservationInsightsFilter = {...filter, workspaceOrAndRoom: selected as IReactSelectValue};
            getSortedAndFilteredOptions(newFilter);

            setFilter({ ...filter, workspaceOrAndRoom: newFilter.workspaceOrAndRoom });
        }
    }

    function updateData(): void {
        setIsDirty(false);
        saveFilterToLocalStorage();
        props.onUpdate(filter);
    }

    function saveFilterToLocalStorage(): void {
        localStorage.setItem('reservationFilter', JSON.stringify(filter));
    }

    function getDefaultFilter(): ReservationInsightsFilter {
        const newFilter = new ReservationInsightsFilter();
        const defaultFilter = {
            ...newFilter,
            startDate: new Date(2024, 0, 1),
            endDate: new Date(),
            venues: props.venues.map(venue => ({ value: venue.id, label: venue.displayName })),
            workspaceOrAndRoom: workspaceOrRoomFilterOptions[0],
            startHourOfTheDay: 7,
            endHourOfTheDay: 18
        };

        return defaultFilter;
    }

    function clearFilter(): void {
        const defaultFilter = getDefaultFilter();

        getSortedAndFilteredOptions(defaultFilter);

        setFilter(defaultFilter);
    }

    return (<div className="report-filter">
        {props.loading && <div className="loading">
            <CenteredPageLoader loading={true} />
        </div>}
        {!props.loading && <div className="filter-row">
            <div>
                <DatePicker
                    id="startDate"
                    selected={filter.startDate}
                    onChange={handleStartDateChange}
                    maxDate={filter.endDate}
                    minDate={new Date(2023, 0, 1)}
                    placeholderText={LanguageProvider.getTranslation("datepicker.startdate")}
                    className="datepicker clickable"
                    dateFormat="dd-MM-yyyy"
                    todayButton={LanguageProvider.getTranslation("datepicker.today")}
                />
            </div>
            <div>
                <DatePicker
                    id="endDate"
                    selected={filter.endDate}
                    onChange={handleEndDateChange}
                    maxDate={new Date()}
                    minDate={minEndDate}
                    placeholderText={LanguageProvider.getTranslation("datepicker.enddate")}
                    className="datepicker clickable"
                    dateFormat="dd-MM-yyyy"
                    todayButton={LanguageProvider.getTranslation("datepicker.today")}
                />
            </div>
            <div>
                <MultiSelectCheckBoxes
                    id="weekFilter"
                    allOptions={weekOptions}
                    onChange={handleWeeksChanged}
                    selectedOptions={filter.weeks || []}
                    placeholderLabel={LanguageProvider.getTranslation("pages.realestateutilization.filter.weeks")}
                />
            </div>
            <div>
                <MultiSelectCheckBoxes
                    id="dayOfWeekFilter"
                    allOptions={dayOfWeekOptions}
                    onChange={handleDayOfWeekChanged}
                    selectedOptions={filter.weekdays || []}
                    placeholderLabel={LanguageProvider.getTranslation("pages.realestateutilization.filter.days")}
                />
            </div>
            <div>
                <MultiSelectCheckBoxes
                    id="holidayFilter"
                    allOptions={holidayOptions}
                    onChange={handleHolidayChanged}
                    selectedOptions={filter.holidaysAsList || []}
                    placeholderLabel={LanguageProvider.getTranslation("pages.realestateutilization.filter.holidays")}
                />
            </div>
            <div>
                <Select
                    className="select-wrapper"
                    id="workspaceOrRoomFilter"
                    options={workspaceOrRoomFilterOptions}
                    onChange={handleWorkspaceOrRoomFilterOptionChanged}
                    value={filter.workspaceOrAndRoom!}
                    styles={SelectBoxUtils.getSelectStylesWithDefaultOption(40)}></Select>
            </div>
            <div>
                <div className="hour-slider d-flex align-items-center justify-content-center">
                    <div className="hour-slider-label">{LanguageProvider.getTranslation("pages.realestateutilization.filter.fromtime")}:</div>
                    <SliderComponent
                        domain={[0, 24]}
                        step={1}
                        mode={2}
                        values={[filter.startHourOfTheDay || 0, filter.endHourOfTheDay || 0]}
                        ticksCount={0}
                        disabled={false}
                        onChange={handleHourChanged}
                    />
                </div>
            </div>
        </div>}
        {!props.loading && <div className="filter-row">
            <div>
                <MultiSelectCheckBoxes
                    id="venueFilter"
                    allOptions={venueOptions}
                    onChange={handleVenueChanged}
                    selectedOptions={filter.venues || []}
                    placeholderLabel={LanguageProvider.getTranslation("pages.realestateutilization.filter.venues")}
                />
            </div>
            <div>
                <MultiSelectCheckBoxes
                    id="floorFilter"
                    allOptions={floorOptions}
                    onChange={handleFloorChanged}
                    selectedOptions={filter.floors || []}
                    placeholderLabel={LanguageProvider.getTranslation("pages.realestateutilization.filter.floors")}
                />
            </div>
            <div>
                <MultiSelectCheckBoxes
                    id="departmentFilter"
                    allOptions={departmentOptions}
                    onChange={handleDepartmentChanged}
                    selectedOptions={filter.departments || []}
                    placeholderLabel={LanguageProvider.getTranslation("pages.realestateutilization.filter.departments")}
                />
            </div>
            <div>
                <MultiSelectCheckBoxes
                    id="placeFilter"
                    allOptions={placeOptions}
                    onChange={handlePlaceChanged}
                    selectedOptions={filter.workPlaces?.concat(filter.rooms || []) || []}
                    placeholderLabel={LanguageProvider.getTranslation("pages.realestateutilization.filter.places")}
                />
            </div>
            <div>
                <MultiSelectCheckBoxes
                    id="placeTypeFilter"
                    allOptions={placeTypeOptions}
                    onChange={handlePlaceTypesChanged}
                    selectedOptions={filter.allPlaceTypes || []}
                    placeholderLabel={LanguageProvider.getTranslation("pages.realestateutilization.filter.placeTypes")}
                />
            </div>
            <div>
                <button type="button" className="btn btn-secondary clear-filter" onClick={clearFilter}>
                    <img src={Tag_Delete} />
                </button>
                {isDirty && <button type="button" className="btn btn-secondary refresh-filter" onClick={updateData}>
                    <img src={refresh} />
                    <span className="notification-dot"></span>
                </button>}
                {!isDirty && <button type="button" disabled className="btn btn-secondary refresh-filter">
                    <img src={refresh} />
                </button>}
            </div>
        </div>}
    </div>);
}

export default ReportFilter;